import { Navigate } from 'react-router-dom';

import { useIsFirstSession } from '@/hooks/useIsFirstSession';
import { Loader } from '@/components/ui/loader';

// Mirrors PrivateRoute's `?nockerk=1` escape: when the bypass is armed,
// Clerk's `useAuth().isLoaded` never flips true, so `isResolving` would trap
// us on the loader. Treat preview as "not first session" instead.
const devClerkBypassActive = () => {
	if (!import.meta.env.DEV || typeof window === 'undefined') return false;
	try {
		return window.sessionStorage.getItem('unify.dev-bypass-clerk') === '1';
	} catch {
		return false;
	}
};

/**
 * Redirects first-login Unify users to `/welcome` before they reach the
 * dashboard. Wraps the authed app routes (inside `PrivateRoute`); `/welcome`
 * itself is intentionally NOT wrapped, so the gate never bounces it.
 *
 *   - impersonating (HQ "view as") → never redirect; render the dashboard so
 *     staff see a fresh retailer's dashboard normally
 *   - still resolving welcome state → hold on a loader (avoids flashing the
 *     dashboard and then bouncing to /welcome)
 *   - first session, not yet dismissed → redirect to /welcome
 *   - otherwise → render the requested route
 */
export function WelcomeGate({ children }) {
	const { isFirstSession, isImpersonating, isResolving } = useIsFirstSession();

	if (devClerkBypassActive()) return children;
	if (isImpersonating) return children;
	if (isResolving) return <Loader label="Loading your dashboard…" />;
	if (isFirstSession) return <Navigate to="/welcome" replace />;
	return children;
}
